/**
 * Tracks the current route as a page_view event whenever the user navigates,
 * and pulls a context-aware next-step suggestion every 5 minutes (or when
 * route changes after enough activity). Suggestion appears as a small,
 * dismissable toast in the bottom-right corner.
 *
 * Anonymous users are no-ops. Tracking respects the user's `show_suggestions`
 * preference and dismissed_suggestions list.
 */
import { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { userPrefs as prefsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Lightbulb, X } from "lucide-react";

import { log } from "@/lib/log";
const FETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const SHOW_DELAY_MS = 60 * 1000; // 60s after page-view before nudge appears

// Map url path → event_type. The path itself becomes the `ref`.
function eventTypeFor(pathname) {
  if (pathname.startsWith("/tools/")) return "tool_used";
  if (pathname.startsWith("/library/")) return "framework_opened";
  if (pathname.startsWith("/jobs")) return "job_viewed";
  if (pathname.startsWith("/eval")) return "eval_run";
  if (pathname.startsWith("/speakers/") || pathname === "/speakers") return "speaker_viewed";
  if (pathname.startsWith("/help")) return "help_read";
  return "page_view";
}

export default function UsageTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const [suggestion, setSuggestion] = useState(null);
  const [visible, setVisible] = useState(false);

  // Beacon current route on every navigation.
  useEffect(() => {
    if (!user) return;
    const eventType = eventTypeFor(location.pathname);
    prefsApi.track(eventType, location.pathname);
  }, [location.pathname, user]);

  const fetchSuggestion = useCallback(async () => {
    if (!user) return;
    try {
      const r = await prefsApi.nextStep();
      setSuggestion(r?.suggestion || null);
    } catch (err) {
      log.warn("[suggestions] fetch failed:", err?.message);
    }
  }, [user]);

  // Poll the next-step suggestion endpoint periodically.
  useEffect(() => {
    if (!user) return;
    fetchSuggestion();
    const t = setInterval(fetchSuggestion, FETCH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [user, fetchSuggestion]);

  // Show after a delay on each route change (so we don't spam on every nav).
  useEffect(() => {
    if (!suggestion || !user) {
      setVisible(false);
      return;
    }
    setVisible(false);
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [suggestion, location.pathname, user]);

  const dismiss = async (forever) => {
    setVisible(false);
    if (forever && suggestion?.id) {
      try {
        // Add to dismissed_suggestions so it won't reappear.
        const prefs = await prefsApi.get();
        const next = [...new Set([...(prefs?.dismissed_suggestions || []), suggestion.id])];
        await prefsApi.update({ dismissed_suggestions: next });
        setSuggestion(null);
      } catch (err) {
        log.warn("[suggestions] dismiss failed:", err?.message);
      }
    }
  };

  if (!user || !suggestion || !visible) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-40 max-w-sm rounded-2xl border border-emerald-400/40 bg-rp-surface shadow-2xl shadow-emerald-900/30 p-4 animate-fade-in"
      data-testid="next-step-toast"
    >
      <button
        type="button"
        onClick={() => dismiss(false)}
        className="absolute top-2 right-2 rounded-full p-1 hover:bg-rp-bg"
        aria-label="Dismiss this nudge"
        data-testid="next-step-dismiss-once"
      >
        <X className="h-3 w-3 text-rp-textFaint" />
      </button>
      <div className="flex items-start gap-2 mb-2">
        <Lightbulb className="h-4 w-4 text-emerald-300 shrink-0 mt-0.5" />
        <div className="min-w-0 pr-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-mono mb-1">Suggested next step</div>
          <div className="text-sm font-semibold text-rp-text" data-testid="next-step-title">{suggestion.title}</div>
          <div className="text-xs text-rp-textDim mt-1">{suggestion.summary}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <Link
          to={suggestion.cta_url}
          onClick={() => dismiss(false)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 px-3 py-1.5 text-xs font-semibold text-white"
          data-testid="next-step-cta"
        >
          {suggestion.cta_label} <ArrowRight className="h-3 w-3" />
        </Link>
        <button
          type="button"
          onClick={() => dismiss(true)}
          className="text-[11px] text-rp-textFaint hover:text-rp-textDim"
          data-testid="next-step-dismiss-forever"
        >
          Don&apos;t show again
        </button>
      </div>
    </div>
  );
}
